import { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import { useGetTeamUsageDetailsQuery } from '../../../hooks/queries/useGetTeamUsageDetailsQuery';
import { Spinner } from '../../../components/Spinner';
import { Heading } from '../../../components/Heading';
import { Alert } from '../../../components/Alert/Alert';
import { Routes } from '../../../enums/Routes';
import { OrganizationLayout } from '../../../components/OrganizationLayout';
import { PrimaryStatCard } from './components/PrimaryStatCard';
import { SimpleChannelDistribution } from './components/SimpleChannelDistribution';
import { ResponseRateTrendChart } from './components/ResponseRateTrendChart';
import { ChannelTagsDisplay } from './components/ChannelTagsDisplay';
import { StatusTag } from './components/StatusTag';
import { SurveyStatusTag } from './components/SurveyStatusTag';
import { AutomationStatsCard } from './components/AutomationStatsCard';
import { DateFormatter } from './utils/DateFormatter';
import { FrequencyFormatter } from './utils/FrequencyFormatter';
import classes from './TeamUsageDetailsPage.module.css';

export const TeamUsageDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const periodStart = searchParams.get('periodStart') || undefined;
  const periodEnd = searchParams.get('periodEnd') || undefined;

  const {
    data: teamDetails,
    error,
    isLoading,
  } = useGetTeamUsageDetailsQuery(teamId || '', periodStart, periodEnd);

  const formattedPeriod = useMemo(() => {
    if (!teamDetails || !teamDetails.periodStart || !teamDetails.periodEnd) {
      return t('usage.details.allTime');
    }
    return `${DateFormatter.formatLong(teamDetails.periodStart, i18n.language)} - ${DateFormatter.formatLong(teamDetails.periodEnd, i18n.language)}`;
  }, [teamDetails, i18n.language, t]);

  const handleGoBack = () => {
    const params = new URLSearchParams();
    if (periodStart) {
      params.set('periodStart', periodStart);
    }
    if (periodEnd) {
      params.set('periodEnd', periodEnd);
    }
    const query = params.toString();
    const backUrl = query
      ? `/organization/usage?${query}`
      : '/organization/usage';
    navigate(backUrl);
  };

  const breadcrumbs = [
    { label: t('organizationPage.tabs.usage'), to: Routes.OrganizationUsage },
    { label: teamDetails?.teamName ?? t('breadcrumbs.team'), to: '' },
  ];

  if (error) {
    return (
      <OrganizationLayout breadcrumbs={breadcrumbs}>
        <div className={classes.content}>
          <Alert title={error.message} variant="danger" size="lg">
            {t('usage.details.error.message')}
          </Alert>
        </div>
      </OrganizationLayout>
    );
  }

  if (isLoading) {
    return (
      <OrganizationLayout breadcrumbs={breadcrumbs}>
        <div className={classes.content}>
          <Spinner label={t('usage.details.loading')} />
          <p>{t('usage.details.loadingShort')}</p>
        </div>
      </OrganizationLayout>
    );
  }

  if (!teamDetails) {
    return null;
  }

  return (
    <OrganizationLayout breadcrumbs={breadcrumbs}>
      <div className={classes.content}>
        <div className={classes.header}>
          <button onClick={handleGoBack} className={classes.backButton}>
            <ArrowLeftIcon fontSize="1rem" aria-hidden />
            {t('usage.details.back')}
          </button>
          <div className={classes.headerContent}>
            <Heading as="h2">{teamDetails.teamName}</Heading>
            <p className={classes.period}>{formattedPeriod}</p>
          </div>
        </div>

        {/* Overview Statistics */}
        <ul className={classes.statsGrid}>
          <PrimaryStatCard
            label={t('usage.details.stats.totalSurveys')}
            value={teamDetails.totalSurveys}
            icon="calendar"
          />
          <PrimaryStatCard
            label={t('usage.details.stats.totalResponses')}
            value={teamDetails.totalResponses}
            icon="chat"
          />
          <PrimaryStatCard
            label={t('usage.details.stats.averageResponseRate')}
            value={`${teamDetails.averageResponseRate.toFixed(1)}%`}
            icon="people"
          />
          <PrimaryStatCard
            label={t('usage.details.stats.activeScheduledSurveys')}
            value={`${teamDetails.activeScheduledSurveys} / ${teamDetails.totalScheduledSurveys}`}
            icon="calendar"
          />
        </ul>

        {/* Automation & Time-Saving */}
        <div className={classes.section}>
          <AutomationStatsCard
            stats={teamDetails.automationStats}
            translations={{
              title: t('usage.details.automation.title'),
              description: t('usage.details.automation.description'),
              remindersSent: t('usage.details.automation.remindersSent'),
              deadlineExtensions: t(
                'usage.details.automation.deadlineExtensions',
              ),
              resultsShared: t('usage.details.automation.resultsShared'),
              actionPlanInvitations: t(
                'usage.details.automation.actionPlanInvitations',
              ),
              actionPlanNotifications: t(
                'usage.details.automation.actionPlanNotifications',
              ),
              aiRecommendationsGenerated: t(
                'usage.details.automation.aiRecommendationsGenerated',
              ),
            }}
          />
        </div>

        {/* Channel Distribution */}
        <div className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('usage.details.channels.title')}
          </h2>
          <SimpleChannelDistribution data={teamDetails.channelDistribution} />
        </div>

        {/* Response Rate Trend Chart */}
        <div className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('usage.details.trend.title')}
          </h2>
          <ResponseRateTrendChart
            data={teamDetails.responseRateTrend}
            language={i18n.language}
          />
        </div>

        {/* Scheduled Surveys */}
        <div className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('usage.details.scheduled.title')}
          </h2>
          <div className={classes.historyTable}>
            <table>
              <thead>
                <tr>
                  <th>{t('usage.details.scheduled.type')}</th>
                  <th>{t('usage.details.scheduled.frequency')}</th>
                  <th>{t('usage.details.scheduled.channel')}</th>
                  <th>{t('usage.details.scheduled.status')}</th>
                  <th>{t('usage.details.scheduled.nextScheduled')}</th>
                </tr>
              </thead>
              <tbody>
                {teamDetails.scheduledSurveys.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={classes.noData}>
                      {t('usage.details.scheduled.noScheduled')}
                    </td>
                  </tr>
                ) : (
                  teamDetails.scheduledSurveys.map((scheduled) => (
                    <tr key={scheduled.id}>
                      <td>
                        {t(
                          `usage.details.history.surveyType.${scheduled.surveyType}`,
                        )}
                      </td>
                      <td>
                        {FrequencyFormatter.format(
                          scheduled.frequency,
                          i18n.language,
                        )}
                      </td>
                      <td>
                        <ChannelTagsDisplay
                          channelsString={scheduled.channel}
                        />
                      </td>
                      <td>
                        <StatusTag
                          isActive={scheduled.isActive}
                          activeText={t('usage.details.scheduled.active')}
                          inactiveText={t('usage.details.scheduled.inactive')}
                        />
                      </td>
                      <td>
                        {scheduled.nextScheduledDate
                          ? DateFormatter.formatLong(
                              scheduled.nextScheduledDate,
                              i18n.language,
                            )
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Survey History */}
        <div className={classes.section}>
          <h2 className={classes.sectionTitle}>
            {t('usage.details.history.title')}
          </h2>
          <div className={classes.historyTable}>
            <table>
              <thead>
                <tr>
                  <th>{t('usage.details.history.sentDate')}</th>
                  <th>{t('usage.details.history.type')}</th>
                  <th>{t('usage.details.history.channel')}</th>
                  <th>{t('usage.details.history.status')}</th>
                  <th>{t('usage.details.history.recipients')}</th>
                  <th>{t('usage.details.history.responses')}</th>
                  <th>{t('usage.details.history.responseRate')}</th>
                </tr>
              </thead>
              <tbody>
                {teamDetails.surveyHistory.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--ds-color-neutral-text-subtle)',
                      }}
                    >
                      {t('usage.details.history.noHistory')}
                    </td>
                  </tr>
                ) : (
                  teamDetails.surveyHistory.map((survey) => (
                    <tr key={survey.id}>
                      <td>
                        {DateFormatter.formatLong(
                          survey.sentDate,
                          i18n.language,
                        )}
                      </td>
                      <td>
                        {t(
                          `usage.details.history.surveyType.${survey.surveyType}`,
                        )}
                      </td>
                      <td>
                        <ChannelTagsDisplay channelsString={survey.channel} />
                      </td>
                      <td>
                        <SurveyStatusTag
                          status={survey.status}
                          translations={{
                            ongoing: t('usage.details.history.statusOngoing'),
                            completed: t(
                              'usage.details.history.statusCompleted',
                            ),
                            expired: t('usage.details.history.statusExpired'),
                          }}
                        />
                      </td>
                      <td>{survey.recipientCount}</td>
                      <td>{survey.responsesReceived}</td>
                      <td>{survey.responseRate.toFixed(1)}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </OrganizationLayout>
  );
};
